import { css, DefaultTheme } from 'styled-components';

import { SellerTheme as SellerProcessedTheme } from '@abbiamo/seller-theme-utils';

export interface HermesTheme extends SellerProcessedTheme {
  logo_link: string;
  favicon: string;
  logo_bg: string;
  carrier_name: string;
}

export const theme = {
  type: 'light',
  bg: '#fff',
  gray: {
    '100': '#F1F1F1',
    '200': '#D1D1D1',
    '300': '#858585',
    '400': '#5A5A5A',
    '500': '#3C3B3B',
  },
  blue: {
    '100': '#DDE9F6',
  },
  badgeBg: {
    DISPATCHED: '#046061',
    CREATED: '#AB6800',
    START_DELIVERY: '#1A76D2',
    CANCELED: '#626262',
    SUCCESSFUL: '#1AA251',
    FAILED: '#C70011',
    PENDING: '#78909c',
    HANDLING: '#1A76D2',
    COLLECTED: '#1A76D2',
    MANUAL_HANDLE: '#626262',
  },
  badgeColor: {
    DISPATCHED: '#046061',
    CREATED: '#AB6800',
    START_DELIVERY: '#1A76D2',
    CANCELED: '#626262',
    SUCCESSFUL: '#1AA251',
    FAILED: '#C70011',
    PENDING: '#78909c',
    HANDLING: '#1A76D2',
    COLLECTED: '#1A76D2',
    MANUAL_HANDLE: '#626262',
  },
  statusColor: {
    START_DELIVERY: '#1976d2',
    CREATED: '#C5861F',
    ALL_WAYPOINTS_FINISHED: '#1A76D2',
    CANCELED: '#626262',
    FINISHED: '#00e676',
  },
  statusBorderColor: {
    START_DELIVERY: '#1976d2',
    CREATED: '#C5861F',
    ALL_WAYPOINTS_FINISHED: '#1A76D2',
    CANCELED: '#626262',
    FINISHED: '#00e676',
  },
  statusBg: {
    START_DELIVERY: '#1976d2',
    CREATED: '#C5861F',
    ALL_WAYPOINTS_FINISHED: '#1A76D2',
    CANCELED: '#626262',
    FINISHED: '#00e676',
  },
  progressBarColor: {
    RemainingBar: '#626262',
    FailedBar: '#C70011',
    SuccessfulBar: '#1AA251',
  },
  badgeBorder: {
    DISPATCHED: '#046061',
    CREATED: '#AB6800',
    START_DELIVERY: '#1A76D2',
    CANCELED: '#626262',
    SUCCESSFUL: '#1AA251',
    FAILED: '#C70011',
    PENDING: '#78909c',
    HANDLING: '#1A76D2',
    COLLECTED: '#1A76D2',
    MANUAL_HANDLE: '#626262',
  },
  colors: {
    blue: {
      500: '#34d8eb',
    },
    main: {
      500: '#6ED796',
    },
    green: {
      500: '#6ED796',
      400: '#12B76A',
    },
    red: {
      500: '#f44336',
    },
    black: {
      600: '#565c5e',
      700: '#3C4042',
      800: '#2D3133',
      900: '#202124',
      950: '#101010',
      1000: '#000',
    },
    white: {
      500: '#F0F0F0',
      700: '#9A9A9A',
      900: '#b7b7b7',
      950: '#DDDDDD',
      1000: '#fff',
    },
    gray: {
      300: '#efefef',
      500: '#DEDEDE',
      800: '#3e3e3e',
    },
  },
  customScroll: (theme: any) => css`
    ::-webkit-scrollbar {
      width: auto;
      padding: 0 3px;
    }

    ::-webkit-scrollbar-track {
      background: ${theme.gray[100]};
    }

    ::-webkit-scrollbar-thumb {
      background: ${theme.gray[200]};
      border: 4px solid ${theme.gray[100]};
    }

    ::-webkit-scrollbar-thumb:hover {
      background: ${theme.seller.primary.safeContainerColor};
    }
  `,
  calloutBg: {
    DEFAULT: '#fff7ed',
    INFO: '#dbeafe',
    ERROR: '#fee2e2',
    WARNING: '#fefce8',
  },
  calloutColor: {
    DEFAULT: '#9a3412',
    INFO: '#1e3a8a',
    ERROR: '#7f1d1d',
    WARNING: '#713f12',
  },
  calloutBorder: {
    DEFAULT: '#ffedd5',
    INFO: '#bfdbfe',
    ERROR: '#fecaca',
    WARNING: '#fef9c3',
  },
};

export type ITheme = typeof theme & {
  seller: HermesTheme;
} & { type: string };

const lightGrays = {
  '100': '#F1F1F1',
  '200': '#D1D1D1',
  '300': '#858585',
  '400': '#5A5A5A',
  '500': '#3C3B3B',
};

const darkGrays = {
  '100': '#4E4E4E',
  '200': '#686868',
  '300': '#bababa',
  '400': '#d9d9d9',
  '500': '#fff',
};

const lightBlue = {
  '100': '#98C9FF',
};

const darkBlue = {
  '100': '#1A76D2',
};

const lightBadge = {
  DISPATCHED: '#FFFFFF',
  CREATED: '#FFFFFF',
  START_DELIVERY: '#FFFFFF',
  CANCELED: '#FFFFFF',
  SUCCESSFUL: '#FFFFFF',
  FAILED: '#FFFFFF',
  PENDING: '#FFFFFF',
  HANDLING: '#FFFFFF',
  COLLECTED: '#FFFFFF',
  MANUAL_HANDLE: '#FFFFFF',
};
const lightBgBadge = {
  DISPATCHED: 'transparent',
  CREATED: 'transparent',
  START_DELIVERY: 'transparent',
  CANCELED: 'transparent',
  SUCCESSFUL: 'transparent',
  FAILED: 'transparent',
  PENDING: 'transparent',
  HANDLING: 'transparent',
  COLLECTED: 'transparent',
  MANUAL_HANDLE: 'transparent',
};
const darkBadgeBorder = {
  DISPATCHED: '#046061',
  CREATED: '#AB6800',
  START_DELIVERY: '#1A76D2',
  CANCELED: '#626262',
  SUCCESSFUL: '#1AA251',
  FAILED: '#C70011',
  PENDING: '#78909c',
  HANDLING: '#1A76D2',
  COLLECTED: '#1A76D2',
  MANUAL_HANDLE: '#626262',
};

const darkBadge = {
  DISPATCHED: '#046061',
  CREATED: '#AB6800',
  START_DELIVERY: '#1A76D2',
  CANCELED: '#626262',
  SUCCESSFUL: '#1AA251',
  FAILED: '#C70011',
  PENDING: '#78909c',
  HANDLING: '#1A76D2',
  COLLECTED: '#1A76D2',
  MANUAL_HANDLE: '#626262',
};
const lightStatus = {
  START_DELIVERY: '#fff',
  CREATED: '#fff',
  ALL_WAYPOINTS_FINISHED: '#fff',
  CANCELED: '#fff',
  FINISHED: '#fff',
};

const darkStatus = {
  START_DELIVERY: '#0277bd',
  CREATED: '#C5861F',
  ALL_WAYPOINTS_FINISHED: '#1a237e',
  CANCELED: '#9e9e9e',
  FINISHED: '#1b5e20',
};

const lightBgStatus = {
  START_DELIVERY: 'transparent',
  CREATED: 'transparent',
  ALL_WAYPOINTS_FINISHED: 'transparent',
  CANCELED: 'transparent',
  FINISHED: 'transparent',
};

const lightBgCallout = {
  DEFAULT: '#fff7ed',
  INFO: '#dbeafe',
  ERROR: '#fee2e2',
  WARNING: '#fefce8',
};

const darkBgCallout = {
  DEFAULT: '#fb923c4D',
  INFO: '#1e3a8a4D',
  ERROR: '#7f1d1d4D',
  WARNING: '#a162074D',
};

const lightCallout = {
  DEFAULT: '#9a3412',
  INFO: '#1e3a8a',
  ERROR: '#7f1d1d',
  WARNING: '#713f12',
};

const darkCallout = {
  DEFAULT: '#fdba74',
  INFO: '#bfdbfe',
  ERROR: '#fecaca',
  WARNING: '#fef08a',
};

const lightCalloutBorder = {
  DEFAULT: '#ffedd5',
  INFO: '#bfdbfe',
  ERROR: '#fecaca',
  WARNING: '#fef9c3',
};

const darkCalloutBorder = {
  DEFAULT: '#fb923c4D',
  INFO: '#bfdbfe4D',
  ERROR: '#fecaca4D',
  WARNING: '#fef08a4D',
};

export const getTheme = (type: 'light' | 'dark'): typeof theme => {
  return {
    ...theme,
    type,
    bg: type === 'light' ? 'white' : '#323232',
    gray: type === 'light' ? lightGrays : darkGrays,
    blue: type === 'light' ? darkBlue : lightBlue,
    badgeBg: type === 'light' ? lightBgBadge : darkBadge,
    badgeColor: type === 'light' ? darkBadge : lightBadge,
    badgeBorder: type === 'light' ? darkBadge : darkBadgeBorder,
    statusBorderColor: type === 'light' ? darkStatus : darkStatus,
    statusColor: type === 'light' ? darkStatus : lightStatus,
    statusBg: type === 'light' ? lightBgStatus : darkStatus,
    calloutBg: type === 'light' ? lightBgCallout : darkBgCallout,
    calloutColor: type === 'light' ? lightCallout : darkCallout,
    calloutBorder: type === 'light' ? lightCalloutBorder : darkCalloutBorder,
  };
};
